<template>
  <Modal
    ref="dialogRef"
    :title="title"
    :full-height="fullHeight"
    :content-class="fullHeight ? 'settings-list' : ''"
    model-value
    @hide="$emit('hide')"
  >
    <template #title>
      <slot name="title" />
    </template>
    <template
      v-if="!hideSearch && !loading"
      #afterTitle
    >
      <ClearableInput
        v-model="search"
        :label="$t('label.search')"
        dense
        class="q-mb-16"
        :rules="searchRules"
      >
        <template #prepend>
          <q-icon
            name="search"
            color="black"
          />
        </template>
      </ClearableInput>
    </template>
    <GtLoading
      v-if="loading"
      size="32px"
      showing
    />
    <q-list
      v-else
      class="q-px-md-16 q-pb-md-16"
    >
      <q-item
        v-for="(option, index) in filteredOptions"
        :key="index"
        clickable
        @click="onClick(option)"
      >
        <q-item-section
          v-if="$slots.symbol"
          avatar
        >
          <slot
            v-bind="option"
            name="symbol"
          />
        </q-item-section>
        <q-item-section
          v-else-if="option.symbol"
          class="text-bold text-black"
          avatar
        >
          <component
            :is="symbolComponent"
            :code="option.symbol"
          >
            {{ option.symbol }}
          </component>
        </q-item-section>

        <q-item-section>
          {{ option.label }}
        </q-item-section>

        <q-item-section
          v-if="value === option.value"
          side
        >
          <q-icon
            name="done"
            color="positive"
          />
        </q-item-section>
      </q-item>

      <NotFound
        v-if="emptySearch"
        :label="$t('label.try_other')"
      />
    </q-list>
  </Modal>
</template>

<script setup>
import { ref, computed } from 'vue';

import Modal from '~components/Modal';
import NotFound from '~components/NotFound';
import GtLoading from '~components/GtLoading';
import ClearableInput from '~components/ClearableInput';

const props = defineProps({
  title: {
    type: String,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  value: {
    type: [String, Number],
    default: null,
  },
  filterBy: {
    type: Array,
    default: () => ['label'],
  },
  fullHeight: {
    type: Boolean,
    default: true,
  },
  options: {
    type: Array,
    required: true,
  },
  hideSearch: {
    type: Boolean,
    default: false,
  },
  searchRules: {
    type: Array,
    default: () => [],
  },
  symbolComponent: {
    type: [String, Object],
    default: 'div',
  },
  autoclose: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['change', 'hide']);

const dialogRef = ref(null);
const search = ref(null);

const filteredOptions = computed(() => {
  if (!search.value) {
    return props.options;
  }

  return props.options.filter((el) => props.filterBy.some(
    (key) => el[key].toLowerCase().includes(search.value.toLowerCase()),
  ));
});

const emptySearch = computed(() => search.value && !filteredOptions.value?.length);

function onClick({ value }) {
  emit('change', value);
  if (props.autoclose) {
    dialogRef.value.hide();
  }
}
</script>

<style lang="scss">
@media (min-width: $breakpoint-md-min) {
  .settings-list .content-section {
    height: 500px;
    width: 500px;
  }
}
</style>

<style lang="scss" scoped>
.q-item__section--avatar {
  min-width: 84px;
}
</style>
